import {
  ActionIcon,
  Badge,
  Card,
  Center,
  createStyles,
  Drawer,
  Group,
  LoadingOverlay,
  ScrollArea,
  Select,
  SimpleGrid,
  Table,
  Text,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import { IconChevronDown, IconChevronUp, IconSelector } from "@tabler/icons";
import { IconArrowBackUp, IconClipboardCheck } from "@tabler/icons-react";
import axios from "axios";
import ActionButton from "components/controls/ActionButtonNew";
import { customLoader } from "components/utilities/loader";
import ManageDepositSlip from "components/Wallet/ManageDepositSlip";
import { useEffect, useState } from "react";
import { Search, X } from "tabler-icons-react";
import { API, formatNumber, PAGE_SIZE_OPTIONS } from "utiles/url";

const useStyles = createStyles((theme) => ({
  th: {
    padding: "0 !important",
    color: "rgb(20, 61, 89)",
  },

  // New class to specifically target the narrow index column
  tinyCol: {
    width: "45px !important",
    padding: "0 !important",
    textAlign: "center",
  },

  control: {
    width: "100%",
    padding: `${theme.spacing.xs}px ${theme.spacing.md}px`,

    "&:hover": {
      backgroundColor:
        theme.colorScheme === "dark"
          ? theme.colors.dark[6]
          : theme.colors.gray[0],
    },
  },

  icon: {
    width: 21,
    height: 21,
    borderRadius: 21,
  },

  searchContainer: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    width: "100%",
  },
  searchInput: {
    flexGrow: 1,
    paddingRight: "50px",
  },
  searchButton: {
    position: "absolute",
    right: 0,
    borderRadius: "0 4px 4px 0",
    height: "70%",
    width: "40px",
    backgroundColor: "#FF6A00",
    color: "#FFFFFF",
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "14px",
    cursor: "pointer",
  },
}));

function Th({ children, sortable, sorted, reversed, onSort, className }) {
  const { classes, cx } = useStyles();
  const Icon = sorted
    ? reversed
      ? IconChevronUp
      : IconChevronDown
    : IconSelector;

  return (
    <th className={cx(classes.th, className)}>
      <UnstyledButton
        onClick={sortable ? onSort : null}
        className={classes.control}
      >
        <Group position="apart" noWrap>
          <Text weight={500} size="sm">
            {children}
          </Text>
          {sortable && (
            <Center className={classes.icon}>
              <Icon size={14} stroke={1.5} />
            </Center>
          )}
        </Group>
      </UnstyledButton>
    </th>
  );
}

const Wallets = () => {
  const { classes } = useStyles();
  const [size, setSize] = useState("10");
  const [activePage, setActivePage] = useState(1);
  const [total, setTotal] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [Wallets, setWallets] = useState([]);
  const [openedEdit, setOpenedEdit] = useState(false);
  const [editId, setEditId] = useState();
  const [search, setSearch] = useState("");
  const [sortedData, setSortedData] = useState([]);
  const [sortBy, setSortBy] = useState(null);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);

  useEffect(() => {
    fetchData(size);
    localStorage.removeItem("wallets");
    window.dispatchEvent(new Event("wallets-changed"));
  }, []);

  const fetchData = async (pageSize) => {
    setLoading(true);
    try {
      let token = localStorage.getItem("auth_token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(
        `${API}/deposit-slips?page=${activePage}&first=${pageSize}`,
        config,
      );
      if (response.data) {
        setWallets(response.data.data);
        setSortedData(response.data.data);
        setTotal(response.data?.links);
        setTotalPages(response.data.paginatorInfo.lastPage);
        setTotalRecords(response.data.paginatorInfo.total);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageSizeChange = (newSize) => {
    setSize(newSize);
    setActivePage(1);
    fetchData(newSize);
  };

  const handleSearchChange = async () => {
    setLoading(true);
    try {
      let token = localStorage.getItem("auth_token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(
        `${API}/deposit-slips?search=${search}&page=${activePage}&first=${size}`,
        config,
      );
      if (response.data) {
        setWallets(response.data.data);
        setSortedData(response.data.data);
        setTotalPages(response.data.paginatorInfo.lastPage);
        setTotalRecords(response.data.paginatorInfo.total);
      }
    } catch (error) {
      console.error("Error searching:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
    const sorted = [...Wallets].sort((a, b) => {
      if (field === "confirmed_at") {
        return reversed
          ? new Date(b[field]) - new Date(a[field])
          : new Date(a[field]) - new Date(b[field]);
      }
      return reversed
        ? b[field]?.toString().localeCompare(a[field]?.toString())
        : a[field]?.toString().localeCompare(b[field]?.toString());
    });
    setSortedData(sorted);
  };

  const handleChange = (page) => {
    setActivePage(page);
    fetchData(size);
  };

  const handleManageDepositSlip = (id) => {
    setEditId(id);
    setOpenedEdit(true);
  };

  const rows = sortedData?.map((row, rowIndex) => {
    const idx = (activePage - 1) * parseInt(size || "0") + (rowIndex + 1);
    return (
      <tr key={row.id}>
        {/* Applied tinyCol here */}
        <td className={classes.tinyCol}>{idx}</td>
        <td>{row.user?.name}</td>
        <td>{row.user?.roles[0]?.name}</td>
        <td>{row.reference_number}</td>
        <td>
          {formatNumber(Math.ceil(Number(row.amount)))}
          <span style={{ marginLeft: "7px" }}>ETB</span>
        </td>
        <td>
          {row.confirmed_at
            ? new Date(row.confirmed_at).toLocaleDateString()
            : "Not Confirmed"}
        </td>
        <td>
          <Badge variant="light" color={row.confirmed_at ? "green" : "red"}>
            {row.confirmed_at ? "Approved" : "Not Approved"}
          </Badge>
        </td>
        <td>
          <ActionButton
            label={row.confirmed_at ? "Reverse" : "Approve"}
            color={row.confirmed_at ? "deactivate" : "activate"}
            onClick={() => handleManageDepositSlip(`${row.id}`)}
          >
            {row.confirmed_at ? (
              <IconArrowBackUp size={16} />
            ) : (
              <IconClipboardCheck size={16} />
            )}
          </ActionButton>
        </td>
      </tr>
    );
  });

  return loading ? (
    <LoadingOverlay visible={loading} overlayBlur={2} loader={customLoader} />
  ) : (
    <div style={{ width: "98%", margin: "auto" }}>
      <Drawer
        opened={openedEdit}
        onClose={() => setOpenedEdit(false)}
        position="right"
        size="40%"
        padding="xl"
        styles={{ closeButton: { color: "black", marginTop: "50px" } }}
      >
        <ManageDepositSlip
          total={total}
          fetchData={fetchData}
          setTotal={setTotal}
          activePage={size}
          setActivePage={setActivePage}
          setOpenedEdit={setOpenedEdit}
          editId={editId}
        />
      </Drawer>

      <Card shadow="sm" p="lg">
        <ScrollArea>
          <SimpleGrid cols={3} mb="md">
            <div />
            <div />
            <div className={classes.searchContainer}>
              <TextInput
                placeholder="Search"
                icon={<Search size={14} />}
                value={search}
                onChange={(event) => setSearch(event.currentTarget.value)}
                className={classes.searchInput}
                rightSection={
                  search && (
                    <UnstyledButton
                      onClick={() => {
                        setSearch("");
                        fetchData(size);
                      }}
                    >
                      <X size={16} color="red" />
                    </UnstyledButton>
                  )
                }
              />
              <button
                className={classes.searchButton}
                onClick={handleSearchChange}
              >
                <Search size={16} />
              </button>
            </div>
          </SimpleGrid>

          <Table
            highlightOnHover
            horizontalSpacing="md"
            verticalSpacing="xs"
            sx={{ tableLayout: "fixed", minWidth: 700 }}
          >
            <thead>
              <tr style={{ backgroundColor: "#F1F1F1" }}>
                {/* Applied tinyCol here */}
                <Th className={classes.tinyCol} sortable={false}>
                  #
                </Th>
                <Th sortable={false}>User</Th>
                <Th sortable onSort={() => handleSort("user_type")}>
                  User Type
                </Th>
                <Th sortable={false}>Reference Number</Th>
                <Th sortable onSort={() => handleSort("amount")}>
                  Amount
                </Th>
                <Th sortable onSort={() => handleSort("confirmed_at")}>
                  Date
                </Th>
                <Th sortable={false}>Status</Th>
                <Th sortable={false}>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {rows?.length > 0 ? (
                rows
              ) : (
                <tr>
                  <td colSpan={8}>
                    <Text align="center">Nothing found</Text>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: "12px",
              padding: "6px 4px",
              borderTop: "1px solid #e9ecef",
              flexWrap: "wrap",
              gap: "8px",
            }}
          >
            {/* Left: Total Records */}
            <Text size="sm" style={{ color: "#FF6A00" }}>
              {`Total Records: ${formatNumber(totalRecords)}`}
            </Text>

            {/* Right: Rows select + navigation */}
            <Group spacing={6} noWrap>
              <Text size="sm" color="dimmed">
                Rows
              </Text>
              <Select
                value={size}
                onChange={handlePageSizeChange}
                data={PAGE_SIZE_OPTIONS}
                style={{ width: 72 }}
                size="xs"
              />
              <ActionIcon
                size="sm"
                variant="default"
                onClick={() => handleChange(1)}
                disabled={activePage <= 1}
                title="First page"
                style={{ fontWeight: 700 }}
              >
                «
              </ActionIcon>
              <ActionIcon
                size="sm"
                variant="default"
                onClick={() => handleChange(activePage - 1)}
                disabled={activePage <= 1}
                title="Previous page"
              >
                ‹
              </ActionIcon>
              <Text size="sm" style={{ minWidth: 40, textAlign: "center" }}>
                {activePage} / {totalPages}
              </Text>
              <ActionIcon
                size="sm"
                variant="default"
                onClick={() => handleChange(activePage + 1)}
                disabled={activePage >= totalPages}
                title="Next page"
              >
                ›
              </ActionIcon>
              <ActionIcon
                size="sm"
                variant="default"
                onClick={() => handleChange(totalPages)}
                disabled={activePage >= totalPages}
                title="Last page"
                style={{ fontWeight: 700 }}
              >
                »
              </ActionIcon>
            </Group>
          </div>
        </ScrollArea>
      </Card>
    </div>
  );
};

export default Wallets;
